import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, LogOut, RefreshCw, Trash2 } from "lucide-react";
import { LogoFull } from "@/components/Logo";
import { API } from "@/lib/api";

const statuses = ["new", "scheduled", "completed"];
const statusStyle = {
  new: "bg-terra/10 text-terra border-terra/30",
  scheduled: "bg-flteal/10 text-flteal border-flteal/30",
  completed: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
};

const inputCls =
  "w-full rounded-xl border border-sandline bg-paper px-4 py-3 text-sm text-obsidian outline-none focus:border-terra placeholder:text-slate-400";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("sp_token") || "");
  const [leads, setLeads] = useState(null);
  const [filter, setFilter] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (t = token, f = filter) => {
    if (!t) return;
    try {
      const { data } = await axios.get(`${API}/leads${f ? `?status=${f}` : ""}`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setLeads(data.leads);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("sp_token");
        setToken("");
        setLeads(null);
      } else {
        toast.error("Could not load leads");
      }
    }
  }, [token, filter]);

  useEffect(() => { load(); }, [load]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("sp_token", data.access_token);
      setToken(data.access_token);
      load(data.access_token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(`${API}/leads/${id}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      setLeads((cur) => cur.map((l) => (l.id === id ? data : l)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/leads/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setLeads((cur) => cur.filter((l) => l.id !== id));
      toast.success("Lead deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-obsidian noise relative flex items-center justify-center px-5">
        <div className="absolute inset-0 blueprint-grid" />
        <motion.form
          onSubmit={login}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="relative w-full max-w-sm bg-sand rounded-2xl p-8 shadow-2xl"
          data-testid="admin-login-form"
        >
          <div className="flex justify-center mb-6"><LogoFull plaque /></div>
          <h1 className="font-display font-bold text-xl text-obsidian text-center mb-6">Admin Leads Panel</h1>
          <div className="space-y-3.5">
            <input data-testid="admin-email-input" type="email" required className={inputCls} placeholder="Admin email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <input data-testid="admin-password-input" type="password" required className={inputCls} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          {error && <p data-testid="admin-login-error" className="mt-3 text-sm text-terra font-medium">{error}</p>}
          <button
            data-testid="admin-login-button"
            disabled={busy}
            className="mt-5 w-full flex items-center justify-center gap-2 bg-terra text-sand font-semibold py-3.5 rounded-full transition-transform hover:scale-[1.02] disabled:opacity-50"
          >
            {busy && <Loader2 size={15} className="animate-spin" />} Sign In
          </button>
          <Link to="/" data-testid="admin-back-home" className="mt-4 flex items-center justify-center gap-1.5 text-xs text-slate-400 hover:text-obsidian">
            <ArrowLeft size={12} /> Back to site
          </Link>
        </motion.form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand" data-testid="admin-dashboard">
      <header className="bg-obsidian text-sand">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
          <LogoFull />
          <div className="flex items-center gap-3">
            <Link to="/" className="font-mono text-[10px] tracking-widest uppercase text-sand/50 hover:text-sand flex items-center gap-1.5">
              <ArrowLeft size={12} /> Site
            </Link>
            <button
              data-testid="admin-logout-button"
              onClick={() => { localStorage.removeItem("sp_token"); setToken(""); setLeads(null); }}
              className="flex items-center gap-1.5 font-mono text-[10px] tracking-widest uppercase border border-sand/20 rounded-full px-3 py-2 hover:text-terra hover:border-terra transition-colors"
            >
              <LogOut size={12} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display font-bold text-2xl sm:text-3xl text-obsidian tracking-tight">Inspection Leads</h1>
            <p className="text-sm text-slate-500 mt-1">{leads ? `${leads.length} request${leads.length === 1 ? "" : "s"}` : "Loading…"}</p>
          </div>
          <div className="flex items-center gap-2">
            {["", ...statuses].map((s) => (
              <button
                key={s || "all"}
                data-testid={`admin-filter-${s || "all"}`}
                onClick={() => setFilter(s)}
                className={`px-4 py-2 rounded-full text-xs font-semibold border transition-colors ${
                  filter === s ? "bg-obsidian text-sand border-obsidian" : "border-sandline text-slate-500 hover:border-obsidian/40"
                }`}
              >
                {s ? s[0].toUpperCase() + s.slice(1) : "All"}
              </button>
            ))}
            <button data-testid="admin-refresh-button" onClick={() => load()} className="p-2.5 rounded-full border border-sandline text-slate-500 hover:text-terra transition-colors">
              <RefreshCw size={15} />
            </button>
          </div>
        </div>

        {!leads ? (
          <div className="flex justify-center py-24"><Loader2 className="animate-spin text-terra" size={28} /></div>
        ) : leads.length === 0 ? (
          <div data-testid="admin-empty-state" className="bg-paper border border-sandline rounded-2xl py-24 text-center text-slate-400 text-sm">
            No leads yet — new booking requests will appear here instantly.
          </div>
        ) : (
          <div className="bg-paper border border-sandline rounded-2xl overflow-x-auto" data-testid="admin-leads-table">
            <table className="w-full text-sm min-w-[900px]">
              <thead>
                <tr className="border-b border-sandline text-left font-mono text-[10px] tracking-[0.2em] uppercase text-slate-400">
                  <th className="px-5 py-4">Client</th>
                  <th className="px-5 py-4">Property</th>
                  <th className="px-5 py-4">Service</th>
                  <th className="px-5 py-4">Schedule</th>
                  <th className="px-5 py-4">Quote</th>
                  <th className="px-5 py-4">Status</th>
                  <th className="px-5 py-4"></th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l, i) => (
                  <tr key={l.id} data-testid={`admin-lead-row-${i + 1}`} className="border-b border-sandline/60 last:border-0 hover:bg-sand/50 transition-colors">
                    <td className="px-5 py-4">
                      <div className="font-semibold text-obsidian">{l.name}</div>
                      <div className="text-xs text-slate-400">{l.phone} · {l.email}</div>
                    </td>
                    <td className="px-5 py-4 text-slate-600 max-w-[220px]">
                      <div className="truncate">{l.address}</div>
                      <div className="text-xs text-slate-400">{Number(l.sqft).toLocaleString()} sq ft</div>
                    </td>
                    <td className="px-5 py-4 text-slate-600 max-w-[180px] truncate">{l.service}</td>
                    <td className="px-5 py-4 text-slate-600 whitespace-nowrap">{l.date}<br /><span className="text-xs text-slate-400">{l.time_slot}</span></td>
                    <td className="px-5 py-4 font-display font-bold text-obsidian">{l.quote_estimate ? `$${l.quote_estimate}` : "—"}</td>
                    <td className="px-5 py-4">
                      <div className="flex gap-1.5">
                        {statuses.map((s) => (
                          <button
                            key={s}
                            data-testid={`admin-status-${s}-${i + 1}`}
                            onClick={() => setStatus(l.id, s)}
                            className={`px-2.5 py-1 rounded-full border text-[10px] font-bold uppercase tracking-wider transition-all ${
                              l.status === s ? statusStyle[s] : "border-sandline text-slate-300 hover:text-slate-500"
                            }`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <button data-testid={`admin-delete-${i + 1}`} onClick={() => remove(l.id)} className="text-slate-300 hover:text-terra transition-colors">
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
